import type { InvoiceTemplate } from "@stb/api-contract"

/**
 * Styles of the paper invoice. One string, two consumers: the page preview (`<style>` next to the
 * document) and the PDF HTML (`<style>` in the head). It must not use Tailwind (the PDF page has
 * none) and must avoid `>`, `&` and quotes, so it survives `renderToStaticMarkup` escaping.
 * The paper is always light: an invoice is printed or emailed, whatever theme the app is in.
 */
export const INVOICE_DOCUMENT_CSS = `
.inv-doc { position: relative; box-sizing: border-box; width: 100%; background: #ffffff; color: #111827; font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; font-size: 13px; line-height: 1.45; padding: 40px 44px; overflow: hidden; }
.inv-doc * { box-sizing: border-box; }
.inv-head { display: flex; justify-content: space-between; gap: 24px; align-items: flex-start; }
.inv-title { margin: 0; font-size: 26px; font-weight: 700; letter-spacing: -0.01em; color: var(--inv-ink, #111827); }
.inv-number { margin: 2px 0 0; font-size: 14px; color: #4b5563; }
.inv-seller { text-align: right; font-size: 12px; color: #374151; }
.inv-seller strong { display: block; font-size: 14px; color: #111827; }
.inv-logo { display: block; max-width: 180px; max-height: 64px; width: auto; height: auto; margin: 0 0 6px auto; }
.inv-tax { display: block; font-size: 10px; color: #6b7280; }
.inv-meta { display: flex; gap: 32px; margin: 28px 0 20px; }
.inv-label { margin: 0 0 2px; font-size: 11px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #6b7280; }
.inv-value { margin: 0; }
.inv-table { width: 100%; border-collapse: collapse; margin-top: 8px; }
.inv-table th { padding: 8px 6px; text-align: left; font-size: 11px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; color: #6b7280; border-bottom: 1px solid #d1d5db; }
.inv-table td { padding: 10px 6px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
.inv-table .inv-num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
.inv-totals { margin: 16px 0 0 auto; width: 260px; }
.inv-totals div { display: flex; justify-content: space-between; padding: 4px 0; font-variant-numeric: tabular-nums; }
.inv-totals .inv-total { margin-top: 6px; padding-top: 8px; border-top: 2px solid var(--inv-accent, #111827); font-size: 16px; font-weight: 700; }
.inv-section { margin-top: 28px; }
.inv-note { white-space: pre-wrap; color: #374151; }
.inv-draft-note { margin-top: 24px; font-size: 12px; color: #6b7280; }
.inv-watermark { position: absolute; top: 38%; left: 50%; transform: translate(-50%, -50%) rotate(-24deg); font-size: 96px; font-weight: 800; letter-spacing: 0.1em; color: rgba(17, 24, 39, 0.06); pointer-events: none; user-select: none; }
.inv-paid { margin-top: 16px; display: inline-block; border: 2px solid #15803d; color: #15803d; border-radius: 6px; padding: 6px 12px; font-weight: 800; letter-spacing: 0.08em; }
.inv-paid span { font-weight: 500; letter-spacing: 0; margin-left: 8px; }
`

/** Modern: brand colour banner, logo (or the name) on the left. Banner text colour is chosen by contrast. */
const MODERN_CSS = `
.inv-t-modern { padding: 0 0 40px; }
.inv-t-modern .inv-head { align-items: center; padding: 28px 44px; background: var(--inv-banner-bg, #374151); color: var(--inv-banner-fg, #ffffff); flex-direction: row-reverse; }
.inv-t-modern .inv-title, .inv-t-modern .inv-number { color: var(--inv-banner-fg, #ffffff); text-align: right; }
.inv-t-modern .inv-seller, .inv-t-modern .inv-seller strong { text-align: left; color: var(--inv-banner-fg, #ffffff); }
.inv-t-modern .inv-logo { margin: 0 0 6px 0; padding: 6px 8px; background: #ffffff; border-radius: 4px; }
.inv-t-modern .inv-meta, .inv-t-modern .inv-table, .inv-t-modern .inv-totals, .inv-t-modern .inv-section, .inv-t-modern .inv-draft-note { margin-left: 44px; margin-right: 44px; width: auto; }
.inv-t-modern .inv-totals { width: 260px; margin-left: auto; }
.inv-t-modern .inv-table th { color: var(--inv-ink, #6b7280); border-bottom: 2px solid var(--inv-accent, #d1d5db); }
`

/** Compact: no decoration, tighter rows so many lines fit one page. */
const COMPACT_CSS = `
.inv-t-compact { font-size: 12px; line-height: 1.3; padding: 28px 32px; }
.inv-t-compact .inv-title { font-size: 20px; }
.inv-t-compact .inv-meta { margin: 16px 0 10px; gap: 24px; }
.inv-t-compact .inv-table th { padding: 5px 4px; }
.inv-t-compact .inv-table td { padding: 5px 4px; }
.inv-t-compact .inv-section { margin-top: 16px; }
.inv-t-compact .inv-logo { max-height: 40px; }
`

const TEMPLATE_CSS: Record<InvoiceTemplate, string> = {
  classic: "",
  modern: MODERN_CSS,
  compact: COMPACT_CSS,
}

/** Base paper styles plus the template's own. */
export function invoiceDocumentCss(template: InvoiceTemplate): string {
  return INVOICE_DOCUMENT_CSS + TEMPLATE_CSS[template]
}

/** Base paper styles once, plus every template's own (several templates shown side by side). */
export function allTemplatesCss(): string {
  return INVOICE_DOCUMENT_CSS + Object.values(TEMPLATE_CSS).join("")
}
